// The studio's side: checking what the editor sends before it's stored, new
// passwords, and the short summary the studio overview lists.
import { SLUG, RESERVED, totals, version, type Approval, type Client, type Env, type Project } from "./auth"

export const PROJECT_ID = /^[a-z0-9][a-z0-9-]{0,40}$/
const DATE = /^\d{4}-\d{2}-\d{2}$/

const str = (v: unknown, max = 300) => String(v ?? "").trim().slice(0, max)
const num = (v: unknown, min = 0, max = 1e7) => {
    const n = Number(v)
    return Number.isFinite(n) ? Math.min(max, Math.max(min, Math.round(n * 100) / 100)) : 0
}
const date = (v: unknown) => (DATE.test(String(v)) ? String(v) : "")

export class Invalid extends Error {}

/** a project as the editor sent it, cleaned; throws Invalid on what can't be stored */
export function cleanProject(raw: any): Project {
    const id = str(raw?.id, 41).toLowerCase()
    if (!PROJECT_ID.test(id)) throw new Invalid(`Project address "${id}" can only have lowercase letters, digits and dashes`)
    const title = str(raw?.title, 120)
    if (!title) throw new Invalid("Every project needs a title")
    const phases = (Array.isArray(raw?.planning?.phases) ? raw.planning.phases : []).slice(0, 40).map((p: any) => ({
        name: str(p?.name, 80),
        start: date(p?.start),
        end: date(p?.end),
        ...(str(p?.note) ? { note: str(p?.note) } : {}),
    }))
    for (const p of phases) {
        if (!p.name || !p.start || !p.end) throw new Invalid(`${title}: every phase needs a name, a start and an end`)
        if (p.end < p.start) throw new Invalid(`${title}: "${p.name}" ends before it starts`)
    }
    if (!phases.length) throw new Invalid(`${title}: the planning needs at least one phase`)
    const milestones = (Array.isArray(raw?.planning?.milestones) ? raw.planning.milestones : [])
        .slice(0, 20)
        .map((m: any) => ({ name: str(m?.name, 80), date: date(m?.date) }))
        .filter((m: any) => m.name && m.date)
    const groups = (Array.isArray(raw?.estimate?.groups) ? raw.estimate.groups : []).slice(0, 30).map((g: any) => ({
        name: str(g?.name, 80),
        lines: (Array.isArray(g?.lines) ? g.lines : []).slice(0, 60).map((l: any) => ({
            item: str(l?.item, 120),
            ...(str(l?.note) ? { note: str(l?.note) } : {}),
            qty: num(l?.qty, 0, 1e5),
            unit: str(l?.unit, 20) || "days",
            rate: num(l?.rate, 0, 1e7),
        })),
    }))
    for (const g of groups) for (const l of g.lines) if (!l.item) throw new Invalid(`${title}: every estimate line needs a name`)
    return {
        id,
        title,
        client: str(raw?.client, 120),
        reference: str(raw?.reference, 40),
        date: date(raw?.date) || new Date().toISOString().slice(0, 10),
        ...(str(raw?.intro, 1200) ? { intro: str(raw?.intro, 1200) } : {}),
        planning: { phases, ...(milestones.length ? { milestones } : {}) },
        estimate: {
            currency: /^[A-Z]{3}$/.test(String(raw?.estimate?.currency)) ? raw.estimate.currency : "EUR",
            vat: num(raw?.estimate?.vat, 0, 100),
            validUntil: date(raw?.estimate?.validUntil),
            groups,
            ...((Array.isArray(raw?.estimate?.notes) ? raw.estimate.notes : []).map((n: unknown) => str(n, 400)).filter(Boolean).length
                ? { notes: raw.estimate.notes.map((n: unknown) => str(n, 400)).filter(Boolean).slice(0, 20) }
                : {}),
        },
    }
}

/** a logo as a small image (data URL), or none */
export function cleanLogo(v: unknown): string | undefined {
    if (v == null || v === "") return undefined
    const s = String(v)
    if (!/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/.test(s)) throw new Invalid("The logo has to be a PNG, JPG or WebP image")
    if (s.length > 400_000) throw new Invalid("The logo is too large")
    return s
}

export function checkSlug(slug: string) {
    if (!SLUG.test(slug) || RESERVED.has(slug)) throw new Invalid(`"${slug}" can't be a client address: lowercase letters, digits and dashes only`)
}

/** 16 characters in groups of 4, easy to type and hard to guess */
export function newPassword() {
    const alphabet = "abcdefghjkmnpqrstuvwxyz23456789"
    const bytes = crypto.getRandomValues(new Uint8Array(16))
    return Array.from(bytes, (b) => alphabet[b % alphabet.length])
        .join("")
        .replace(/(.{4})(?=.)/g, "$1-")
}

/** approvals stay only for estimates that are still exactly what was approved */
export function keptApprovals(projects: Project[], approvals: Record<string, Approval> = {}) {
    const kept: Record<string, Approval> = {}
    for (const p of projects) {
        const a = approvals[p.id]
        if (a && (!a.version || a.version === version(p))) kept[p.id] = a
    }
    return kept
}

/** what the studio overview shows of a client */
export function summary(slug: string, c: Client) {
    return {
        slug,
        name: c.name,
        logo: c.logo || null,
        lang: c.lang === "nl" ? "nl" : "en",
        no: c.no || 0,
        projects: c.projects.map((p) => ({
            id: p.id,
            title: p.title,
            reference: p.reference,
            total: totals(p.estimate).total,
            currency: p.estimate.currency,
            approval: c.approvals?.[p.id] || null,
            start: p.planning.phases.reduce((m, ph) => (ph.start < m ? ph.start : m), p.planning.phases[0]?.start || ""),
            end: p.planning.phases.reduce((m, ph) => (ph.end > m ? ph.end : m), p.planning.phases[0]?.end || ""),
            phases: p.planning.phases.map((ph) => ({ name: ph.name, start: ph.start, end: ph.end })),
        })),
    }
}

/** the default reference of a client's new project: NTO-, the year, the client's number (NTO-2601) */
export const defaultReference = (no: number, date = new Date()) => `NTO-${String(date.getFullYear()).slice(2)}${String(no || 0).padStart(2, "0")}`

/**
 * Every client gets a number, in the order they were made (01, 02, …): it goes
 * into their project references. Numbers stay with a client; a deleted
 * client's number isn't given out again. Clients from before numbers existed
 * get theirs here, in the order of the list.
 */
export async function numberClients(env: Env, list: [string, Client][]) {
    let max = Math.max(0, ...list.map(([, c]) => c.no || 0), Number((await env.PORTAL.get("meta:lastNo")) || 0))
    for (const [slug, c] of [...list].sort((a, b) => a[1].name.localeCompare(b[1].name))) {
        if (c.no) continue
        c.no = ++max
        await env.PORTAL.put(`client:${slug}`, JSON.stringify(c))
    }
    await env.PORTAL.put("meta:lastNo", String(max))
    return max
}
